import React, {useState, useMemo, useEffect} from "react"
import {Link} from "react-router-dom"
import {useQuery} from "react-query"
import {toast} from "react-toastify"
import nprogress from "nprogress"
import useAuth from "auth/useAuth"
import useAreas from "hooks/useAreas"
import useShiftRoster from "hooks/useShiftRoster"
import Rescuer from "components/Rescuer"
import RescuerSelect from "components/rescuer/RescuerSelect"
import useRescuers from "hooks/useRescuers"
import PendingApplication from "./PendingApplication"
import RosterMember from "./RosterMember"
import moment from "moment"
import {plural} from "utils/text_utils"
import "./ShiftRosterPage.css"

const ROLE_ORDER = {head: 0, snowmobiler: 1, member: 2, trainee: 3}

const getDefaultWeek = (weeks) => {
    if (!weeks || weeks.length === 0) return null
    const today = new Date().toISOString().split('T')[0]
    return weeks.find(w => w >= today) || weeks[weeks.length - 1]
}

export default function ShiftRosterPage() {

    const {auth, API} = useAuth()
    const {area, setArea, mountains} = useAreas()
    const {getUser} = useRescuers()
    const {data: weeks} = useQuery("weeks", () => auth(API.shiftsWeeksGet).then(response => response.data), {select: data => data?.weeks ?? []})
    const [selectedWeek, setSelectedWeek] = useState(null)
    const [changes, setChanges] = useState([])
    const [notes, setNotes] = useState({})

    const week = selectedWeek || getDefaultWeek(weeks)
    const {roster, pending, permissions, refetch, isLoading, isFetching} = useShiftRoster(area, week)
    const dataReady = !!roster && !isLoading

    const canEdit = permissions?.edit === true
    const isDirty = changes.length > 0 || Object.keys(notes).length > 0

    console.log('[Roster]', {
        area,
        weeks: weeks ? `${weeks.length} weeks` : weeks,
        selectedWeek,
        week,
        isLoading,
        isFetching,
        roster: roster ? `${roster.members?.length} members` : roster,
        pending: pending ? `${pending.length} pending` : pending,
        dataReady
    })

    // Reset changes on area/week switch
    useEffect(() => {
        setChanges([])
        setNotes({})
    }, [area, week])

    const displayMembers = useMemo(() => {
        let members = [...(roster?.members || [])]
        for (const change of changes) {
            if (change.action === 'confirm' || change.action === 'add') {
                if (!members.find(m => m.username === change.target)) {
                    members.push({username: change.target, role: change.role || 'member', rescuer: change.rescuer})
                }
            } else if (change.action === 'remove') {
                members = members.filter(m => m.username !== change.target)
            } else if (change.action === 'set_role') {
                members = members.map(m => m.username === change.target ? {...m, role: change.role} : m)
            }
        }
        members = members.map(m => ({
            ...m,
            notes: notes[m.username] !== undefined ? notes[m.username] : (m.notes || '')
        }))
        members.sort((a, b) => {
            const ra = ROLE_ORDER[a.role] ?? 99
            const rb = ROLE_ORDER[b.role] ?? 99
            if (ra !== rb) return ra - rb
            const idA = getUser(a.username)?.gss_id || a.username
            const idB = getUser(b.username)?.gss_id || b.username
            return String(idA).localeCompare(String(idB), undefined, {numeric: true})
        })
        return members
    }, [roster, changes, notes, getUser])

    const displayPending = useMemo(() => {
        if (!pending) return []
        const confirmedOrRejected = new Set()
        const restored = new Set()
        for (const change of changes) {
            if (change.action === 'confirm' || change.action === 'reject') {
                confirmedOrRejected.add(change.target)
                restored.delete(change.target)
            } else if (change.action === 'remove' || change.action === 'unreject') {
                restored.add(change.target)
                confirmedOrRejected.delete(change.target)
            }
        }
        const fromPending = pending.filter(app => !confirmedOrRejected.has(app.username))
        const restoredApps = [...restored]
            .filter(username => !fromPending.find(app => app.username === username))
            .map(username => ({username, regular: true}))
        return [...fromPending, ...restoredApps]
    }, [pending, changes])

    const displayRejected = useMemo(() => {
        const unrejected = new Set(changes.filter(c => c.action === 'unreject').map(c => c.target))
        const base = (roster?.rejected || []).filter(u => !unrejected.has(u))
        for (const change of changes) {
            if (change.action === 'reject' && !base.includes(change.target) && !unrejected.has(change.target)) {
                base.push(change.target)
            }
        }
        return base
    }, [roster, changes])

    const onConfirm = (application) => {
        const rescuer = application.rescuer || getUser(application.username)
        setChanges(prev => [...prev, {action: 'confirm', target: application.username, role: 'member', rescuer}])
    }

    const onReject = (application) => {
        setChanges(prev => [...prev, {action: 'reject', target: application.username}])
    }

    const onUnreject = (username) => {
        const hadLocalReject = changes.some(c => c.action === 'reject' && c.target === username)
        if (hadLocalReject) {
            setChanges(prev => prev.filter(c => !(c.action === 'reject' && c.target === username)))
        } else {
            setChanges(prev => [...prev, {action: 'unreject', target: username}])
        }
    }

    const onRemove = (member) => {
        setChanges(prev => [...prev, {action: 'remove', target: member.username}])
    }

    const onSetRole = (member, role) => {
        if (role === 'head') {
            const currentHead = displayMembers.find(m => m.role === 'head')
            if (currentHead && currentHead.username !== member.username) {
                setChanges(prev => [
                    ...prev,
                    {action: 'set_role', target: currentHead.username, role: 'member'},
                    {action: 'set_role', target: member.username, role}
                ])
                return
            }
        }
        setChanges(prev => [...prev, {action: 'set_role', target: member.username, role}])
    }

    const onAddRescuer = (rescuer) => {
        setChanges(prev => [...prev, {action: 'add', target: rescuer.username, role: 'member', rescuer}])
    }

    const onSetNotes = (member, value) => {
        setNotes(prev => ({...prev, [member.username]: value}))
    }

    const onSave = () => {
        const removedUsernames = new Set(changes.filter(c => c.action === 'remove').map(c => c.target))
        const notesActions = Object.entries(notes)
            .filter(([username]) => !removedUsernames.has(username))
            .map(([username, value]) => ({action: 'set_notes', target: username, notes: value}))
        nprogress.start()
        auth(API.shiftsRosterPost, {}, {
            area,
            week,
            actions: [
                ...changes.map(({action, target, role}) => ({action, target, role})),
                ...notesActions
            ]
        }).then(() => {
            setChanges([])
            refetch().then(() => setNotes({}))
            nprogress.done()
        }).catch(err => {
            toast.error(err?.data || "Greška")
            nprogress.done()
        })
    }

    const memberCount = displayMembers.length

    return <div id="roster-page" className="container-fluid shadow">
        <section id="roster-header" className="white page-head">
            <h3>Priprema smene <Link to="/apply" className="roster-back">Prijave</Link></h3>
            <div className="roster-selectors">
                <select value={area} onChange={e => setArea(e.currentTarget.value)}>
                    {mountains.map(a => <option key={a} value={a}>{a}</option>)}
                </select>
                {weeks && <select value={week || ''} onChange={e => setSelectedWeek(e.currentTarget.value)}>
                    {weeks.map(w => <option key={w} value={w}>{moment(w).format("Do MMMM YYYY.")}</option>)}
                </select>}
            </div>
        </section>

        {(isLoading || (week && !dataReady)) && <section className="white"><div id="spinner" className="center"/></section>}

        {dataReady && week && <>
            {displayPending && displayPending.length > 0 && <section className="white pending-section">
                <h3>Prijave ({displayPending.length})</h3>
                {displayPending.map(app => <PendingApplication
                    key={app.username || app.id}
                    application={app}
                    onConfirm={onConfirm}
                    onReject={onReject}
                    canEdit={canEdit}
                />)}
            </section>}

            {displayPending && displayPending.length === 0 && <section className="white pending-section">
                <h3>Prijave</h3>
                <p className="empty-text">Nema prijava za potvrdu</p>
            </section>}

            <section className="white shift-section">
                <h3>Smena ({memberCount})</h3>
                {displayMembers.map((member, index) => <RosterMember
                    key={member.username}
                    member={member}
                    index={index}
                    onRemove={onRemove}
                    onSetRole={onSetRole}
                    canEdit={canEdit}
                    notes={member.notes}
                    onSetNotes={onSetNotes}
                />)}
                {memberCount > 0 && <div className="roster-count">
                    Ukupno: {memberCount} {plural(memberCount, 'spasilac', 'spasioca', 'spasilaca')}
                </div>}
                {memberCount === 0 && <p className="empty-text">Smena je prazna</p>}
            </section>

            {canEdit && <section className="white add-section">
                <h3>Dodaj spasioca</h3>
                <RescuerSelect onClick={onAddRescuer}/>
            </section>}

            {displayRejected.length > 0 && <section className="white rejected-section">
                <h3>Odbijene prijave ({displayRejected.length})</h3>
                {displayRejected.map(username => <Rescuer
                    key={username}
                    rescuer={getUser(username)}
                    onClick={canEdit ? () => onUnreject(username) : undefined}
                />)}
            </section>}

            {canEdit && <div className="roster-save-bar">
                <button className="btn btn-primary" onClick={onSave} disabled={!isDirty}>Snimi</button>
            </div>}
        </>}
    </div>

}
